const en = {
  code: 'en',
  meta: {
    titre: 'Datasectoriel — Global centre for sector-specific video data',
    description: 'Datasectoriel produces, verifies, anonymises and distributes exclusive video corpora for training artificial intelligence and robotics systems.'
  },
  saut: 'Skip to content',
  nav: {
    projets: 'For your projects',
    partenaire: 'Become a partner',
    activites: 'Activities',
    acces: 'Request access',
    accesLong: 'Request access to the platform',
    ouvrir: 'Open menu',
    fermer: 'Close menu',
    langue: 'Language'
  },
  menus: {
    projets: [
      { to: '/#sources', titre: 'Two sources of data', desc: 'Sector experts and capture in real conditions' },
      { to: '/#besoin', titre: 'Data aligned with your objectives', desc: 'The characteristics of the data required' },
      { to: '/#processus', titre: 'A rigorous process', desc: 'From request to delivery' },
      { to: '/#engagements', titre: 'Our commitments', desc: 'Exclusivity, traceability, compliance' }
    ],
    projetsCarte: { to: '/demander-un-acces', eti: 'Platform access', texte: 'Access existing corpora or entrust us with building your own.' },
    partenaire: [
      { to: '/#partenaire', titre: 'Partner companies', desc: 'Involve your teams in our collection programmes' },
      { to: '/#activites', titre: 'Everyday and professional activities', desc: 'The tasks covered by the collection' },
      { to: '/#processus', titre: 'Conditions of participation', desc: 'Instructions, verification, processing and classification' }
    ],
    partenaireCarte: { to: '/devenir-partenaire', eti: 'Company programme', texte: 'Your teams are paid for accepted recordings, with no change to their tasks.' }
  },
  hero: {
    surtitre: 'Global centre for sector-specific video data',
    titre: 'Video data designed for your artificial intelligence and robotics projects',
    chapeau: 'The quality of an artificial intelligence system depends directly on the relevance, diversity and traceability of the data used to train it.',
    cta1: 'Request access to the platform',
    cta2: 'Define your requirement'
  },
  intro: [
    'Datasectoriel provides companies, laboratories and institutions with exclusive video corpora, built from content produced by sector experts and from recordings made while everyday or professional tasks are being performed.',
    'We handle the entire process, from defining the requirement through to making the files available. We organise video production, select contributors, verify recordings, process sensitive elements, classify the data and ensure its distribution in a secure environment.'
  ],
  sources: {
    titre: 'Two complementary sources of data',
    cartes: [
      {
        titre: 'Content produced by sector experts',
        paragraphes: [
          'Datasectoriel relies on professionals whose identity, qualifications and experience are verified.',
          'These experts produce exclusive videos in their field of expertise, in accordance with the requirements defined for each project. The content is produced specifically for Datasectoriel and does not come from videos freely available on the Internet.',
          'This production covers 40 fields of expertise, including medicine, science, industry, aeronautics, automotive, robotics, information technology, cybersecurity, finance, law, agriculture, education, media, culture, sport and the environment.',
          'Every file is verified before integration. Its origin, its author, its production date and its field of attachment are recorded in order to guarantee precise identification of the data made available to you.'
        ]
      },
      {
        titre: 'Tasks recorded in real conditions',
        paragraphes: [
          'Datasectoriel also organises the collection of videos made while everyday or professional tasks are actually being performed.',
          'Recordings are produced by individuals, professionals or the teams of our partner companies. Each participant films a task they genuinely carry out, in their usual environment and according to the instructions established for the project.',
          'Capture is carried out from a first-person point of view, using a smartphone fixed to a head-mounted support or any other device approved by Datasectoriel. It makes it possible to observe the task from the point of view of the person performing it, in particular the actions carried out, the objects handled, the tools used, the order in which they are used and the immediate environment.',
          'This method makes it possible to build series of recordings showing different executions of the same task, in varied places, contexts and conditions.'
        ]
      }
    ]
  },
  besoin: {
    titre: 'Data aligned with your objectives',
    chapeau: 'Every project begins with a precise analysis of your requirement. You determine the characteristics of the data required. On this basis, we determine the most appropriate production method: sector experts, individual contributors, collection from professionals, or a specific programme with one or more partner companies.',
    natureTitre: 'Nature of the content',
    natureAide: 'The source determines how the data is produced. Expert content is defined by a field of expertise; recordings in real conditions are defined by the tasks filmed.',
    sources: { experts: 'Sector experts', reelles: 'Real conditions', deux: 'Both sources' },
    secteurLabel: 'Field of expertise',
    secteurAide: 'The field of competence in which experts produce exclusive videos. Their identity, qualifications and experience are verified.',
    tachesTitre: 'Tasks to be recorded',
    tachesAide: 'Tasks filmed from a first-person point of view while actually being performed, by individuals, professionals or the teams of our partner companies.',
    volumeLabel: 'Expected volume',
    volumeMin: 'Minimum',
    volumeMax: 'Maximum',
    sansLimite: 'No limit',
    valeurExacte: 'Exact value',
    ouPlus: 'or more',
    volumeAide: 'The volume is not capped: enter the value matching your project, the slider is only an estimate.',
    dureeAide: 'The length of the sequences is not capped: enter the value matching your project.',
    dureeLabel: 'Length of sequences',
    dureeCourte: 'Short',
    dureeLongue: 'Long',
    recapTete: 'Summary of your requirement',
    rNature: 'Nature',
    rBlocExperts: 'Content produced by experts',
    rSecteur: 'Field of expertise',
    rBlocReel: 'Tasks recorded in real conditions',
    rTaches: 'Tasks',
    rCommun: 'Common to both sources',
    rCarac: 'Expected characteristics',
    rVolume: 'Volume',
    rSequences: 'Sequences',
    reste: 'The following are also defined with you: the language, the file format, the technical characteristics, the information that must accompany the videos, the quality criteria and the intended use.',
    cta: 'Submit your requirement',
    note: 'You can access existing corpora or entrust us with building data that meets the specific requirements of your project.',
    heure: 'hour',
    heures: 'hours',
    minute: 'minute',
    minutes: 'minutes',
    tacheSel: 'task selected',
    tachesSel: 'tasks selected',
    nombres: ['None','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen']
  },
  activites: {
    titre: 'Everyday and professional activities',
    chapeau: 'Collection programmes may cover a wide range of tasks, including:',
    note: 'This list is not exhaustive. Collection is organised according to the tasks required for your project and the conditions in which they must be recorded.'
  },
  processus: { titre: 'A rigorous process, from request to delivery' },
  partenairePage: {
    titre: 'Become a partner',
    chapeau: 'Involve your teams in our collection programmes.',
    colonne1: [
      'Your company can take part directly in data production by allowing its teams to record certain tasks already carried out as part of its activity.',
      'The programme is defined with your organisation in order to respect your internal procedures, your security requirements, your confidentiality rules and the constraints specific to your sector.',
      'Participants use a lightweight device and follow the instructions established for the collection. The recordings are then transmitted to Datasectoriel, verified, processed and classified before being integrated into the relevant corpora.'
    ],
    colonne2: [
      'The terms of participation, provision of equipment, remuneration and use of the videos are determined within the framework of the partnership concluded with your company.'
    ],
    accent: 'Your organisation and its teams are therefore paid for accepted recordings, with no change to the nature of the tasks already performed.',
    cta: 'Become a partner company'
  },
  enrichissement: {
    titre1: 'Regularly enriched corpora',
    p1: [
      'Requirements relating to artificial intelligence and robotics evolve with projects, sectors and environments of use.',
      'Datasectoriel regularly enriches its corpora by adding new content, new tasks, new languages and new recording contexts.',
      'A collection may be organised over a defined period, in several countries, with different participant profiles or in environments that meet your requirements.',
      'This continuous production makes it possible to gradually increase the volume and diversity of the data available.'
    ],
    titre2: 'Data that can be used directly',
    p2intro: 'Videos recorded in real conditions make it possible to observe precisely:',
    liste: ['the order of actions','the movements required','the handling of objects','the use of tools','the changes occurring during execution','the differences between several environments','the difficulties that may be encountered','the result obtained'],
    apres: [
      'Combined with the content produced by sector experts, they make it possible to bring together data covering both knowledge of a field and the actual performance of the tasks related to it.',
      'These corpora may be used for research and analysis, as well as for training and improving artificial intelligence and robotics systems.'
    ]
  },
  engagementsTitre: 'Our commitments',
  engagements: [
    { titre: 'Exclusivity', texte: 'The videos are produced specifically for Datasectoriel or within the framework of a project entrusted to us by one of our partners.' },
    { titre: 'Verified origin', texte: 'Every contribution comes from an identified person. Where a project requires specific expertise, the qualifications and experience of the participant are verified.' },
    { titre: 'Quality', texte: 'Every file is examined before integration. Only data meeting the established criteria is accepted.' },
    { titre: 'Anonymisation', texte: 'Personal or confidential information, and information unrelated to the purpose of the collection, is removed or masked before the files are distributed.' },
    { titre: 'Traceability', texte: 'Every video is identified, recorded and tracked by means of a unique digital fingerprint.' },
    { titre: 'Security', texte: 'The data is stored and distributed in a secure environment. Access rights are defined for each partner according to the licence taken out.' },
    { titre: 'Compliance', texte: 'The production, processing and distribution of the data are governed so as to comply with the applicable obligations regarding data protection and artificial intelligence.' }
  ],
  acces: {
    titre: 'Entrust us with building your data',
    chapeau: 'Tell us about the objectives of your project, the sectors concerned, the content or tasks required, the volume needed and the intended conditions of use. We define the production arrangements, select the participants, organise the collection, verify the files and provide you with exclusive corpora that are verified, classified and ready to use.',
    actions: [
      { to: '/demander-un-acces', titre: 'Request access to the platform', texte: 'Access existing corpora and their licence conditions.', go: 'Open the request' },
      { to: '/#besoin', titre: 'Entrust us with building a corpus', texte: 'Determine the characteristics of the data required.', go: 'Define the requirement' },
      { to: '/devenir-partenaire', titre: 'Become a partner company', texte: 'Involve your teams in the collection programmes.', go: 'Review the programme' }
    ]
  },
  pied: {
    baseline: 'Global centre for sector-specific video data.',
    projets: 'For your projects',
    partenaires: 'Partners',
    societe: 'Company',
    liensProjets: [
      { to: '/#sources', l: 'Two sources of data' },
      { to: '/#besoin', l: 'Data aligned with your objectives' },
      { to: '/#processus', l: 'A rigorous process' },
      { to: '/demander-un-acces', l: 'Request access' }
    ],
    liensPartenaires: [
      { to: '/devenir-partenaire', l: 'Partner companies' },
      { to: '/#activites', l: 'Everyday and professional activities' },
      { to: '/#engagements', l: 'Our commitments' }
    ],
    liensSociete: [
      { to: '/contact', l: 'Contact' },
      { to: '/mentions-legales', l: 'Legal notice' },
      { to: '/politique-de-confidentialite', l: 'Privacy policy' },
      { to: '/politique-des-cookies', l: 'Cookie policy' },
      { to: '/conditions-generales', l: 'Terms and conditions' }
    ],
    bas: '© Datasectoriel — All rights reserved'
  },
  contact: {
    intro: 'Would you like to access the platform, offer your expertise or obtain further information?',
    invite: 'Please feel free to contact us at one of the following addresses:',
    adresses: ['contact@datasectoriel.com', 'contact@neurialabs.com'],
    cloture: 'Our team is at your disposal to answer your request as quickly as possible.',
    ecrire: 'Write to',
    asideTete: 'Write to us',
    sujetAcces: 'Request for access to the platform',
    sujetPartenaire: 'Partner company programme',
    sujetContact: 'Request for information',
    sujetBesoin: 'Definition of a data requirement'
  },
  form: {
    organisation: 'Organisation',
    nom: 'Full name',
    fonction: 'Role',
    email: 'Email address',
    telephone: 'Telephone',
    pays: 'Country',
    message: 'Your project',
    envoyer: 'Submit the request',
    envoi: 'Submitting…',
    succes: 'Your request has been recorded. It will be reviewed by Datasectoriel.',
    erreur: 'The request could not be recorded. Please try again.',
    requis: 'Please complete the required fields.',
    obligatoire: 'Required fields: organisation, full name, email address.',
    recapTete: 'Information submitted'
  },
  pageAcces: {
    titre: 'Request access to the platform',
    chapeau: 'Access existing corpora and their licence conditions.',
    encadre: 'You can access existing corpora or entrust us with building data that meets the specific requirements of your project.'
  },
  pagePartenaire: {
    titre: 'Become a partner company',
    chapeau: 'Involve your teams in our collection programmes.',
    encadre: 'Your organisation and its teams are therefore paid for accepted recordings, with no change to the nature of the tasks already performed.'
  },
  pageContact: {
    titre: 'Contact',
    chapeau: 'Tell us about the objectives of your project, the sectors concerned, the content or tasks required, the volume needed and the intended conditions of use.',
    encadre: 'Global centre for sector-specific video data.'
  },
  legales: {
    mentions: 'Legal notice',
    confidentialite: 'Privacy policy',
    conditions: 'Terms and conditions of use',
    conditionsBlocs: [
      {
        niveau: 2,
        titre: 'Purpose and scope',
        paragraphes: [
          'The purpose of these Terms and Conditions of Use is to define the terms and conditions under which users access and use the datasectoriel.com website. datasectoriel is a brand of NeuriaLabs, a company specialising in the design, development and integration of software solutions based on artificial intelligence. They set out the rights and obligations of any person browsing the site, whether they are simple visitors or users benefiting from access to specific services.',
          'Access to the site and its use imply full and unreserved acceptance of these terms and conditions. Any person refusing these terms is invited not to use the site.',
          'These terms apply to all users, regardless of status or geographical location, and govern all the digital services offered by datasectoriel and NeuriaLabs.',
          'NeuriaLabs reserves the right to modify these terms at any time, without prior notice. Any modification takes effect as soon as it is published online. It is up to users to consult these terms regularly to ensure that they meet their expectations.'
        ]
      },
      {
        niveau: 2,
        titre: 'Access to the site and availability of services',
        paragraphes: [
          'Access to the site is free and open to any person with an internet connection.',
          'NeuriaLabs implements the necessary means to guarantee continuous access to the site, subject to any maintenance operations, technical updates or interruptions beyond its control. Users acknowledge that the publisher may not be held liable for any temporary interruptions of access related to technical constraints, updates to digital infrastructures or security measures intended to protect the integrity of the site.',
          'Any temporary suspension or prolonged interruption of the site will be carried out in compliance with regulatory obligations and the commitments of datasectoriel and NeuriaLabs towards its users.',
          'Users are solely responsible for their access to the site and undertake to take all necessary precautions to protect their computer equipment against any intrusion, alteration or contamination by malicious software.'
        ]
      },
      {
        niveau: 2,
        titre: 'User obligations',
        paragraphes: [
          'Users undertake to use the site in accordance with these terms and the laws in force. Any abusive or fraudulent use, or use contrary to the applicable regulations, is strictly prohibited.',
          'In particular, users must refrain from:'
        ],
        liste: [
          { libelle: '', texte: 'Undermining the integrity of the site, attempting to access secure sections without authorisation or using tools intended to compromise the stability of the system.' },
          { libelle: '', texte: 'Publishing, transmitting or distributing any content that is unlawful, defamatory, insulting, threatening or contrary to public decency.' },
          { libelle: '', texte: 'Carrying out operations aimed at abusively collecting the personal data of other users.' },
          { libelle: '', texte: 'Misusing the features of the site for purposes not provided for by these terms.' }
        ],
        apres: [
          'Any breach of these obligations may result in the immediate suspension of the user’s access to the site, without prejudice to any legal proceedings that NeuriaLabs reserves the right to bring.',
          'Users also acknowledge that they are responsible for the data they provide to datasectoriel and NeuriaLabs and guarantee its accuracy and updating.'
        ]
      },
      {
        niveau: 2,
        titre: 'Intellectual property',
        paragraphes: [
          'The site and all of its content, including texts, images, videos, graphics, logos, databases and programming elements, are protected by the laws in force regarding intellectual property and are the exclusive property of NeuriaLabs or its partners.',
          'Any reproduction, representation, modification or distribution, in whole or in part, of this content, without the prior and express authorisation of NeuriaLabs, constitutes an infringement punishable by civil and criminal penalties.',
          'The trademarks, trade names and other distinctive signs displayed on the site, including datasectoriel and NeuriaLabs, are also protected. Any use without the express authorisation of NeuriaLabs is prohibited and may give rise to legal proceedings.',
          'Users undertake not to infringe the intellectual property rights of NeuriaLabs and acknowledge that any unauthorised exploitation of the site’s content may result in legal penalties.'
        ]
      },
      {
        niveau: 2,
        titre: 'Liability of NeuriaLabs',
        paragraphes: [
          'NeuriaLabs makes every effort to ensure the reliability and accuracy of the information published on the datasectoriel.com website. However, no express or implied guarantee is given as to the completeness, timeliness and accuracy of the content published online.',
          'Users acknowledge that they use the site under their sole responsibility and accept that NeuriaLabs may not be held liable for:'
        ],
        liste: [
          { libelle: '', texte: 'Any error or omission in the information published.' },
          { libelle: '', texte: 'Any direct or indirect damage resulting from the use of the site, including in the event of data loss, alteration of computer systems or unauthorised access to personal information.' },
          { libelle: '', texte: 'Any temporary or prolonged interruption of the site due to technical constraints, computer attacks or external events beyond its control.' }
        ],
        apres: [
          'NeuriaLabs reserves the right to suspend access to the site at any time and without notice for reasons of maintenance, updating or protection against any technological threat.'
        ]
      },
      {
        niveau: 2,
        titre: 'Protection of personal data',
        paragraphes: [
          'NeuriaLabs undertakes to process users’ personal data in compliance with the regulations in force, in particular the General Data Protection Regulation (GDPR) and the applicable local laws.',
          'The terms of collection, retention and processing of data are detailed in the Privacy Policy accessible on the datasectoriel.com website.',
          'All users have rights over their data, in particular a right of access, rectification, objection and erasure, which they may exercise by contacting datasectoriel or NeuriaLabs at the following address: contact@datasectoriel.com or contact@neurialabs.com',
          'No personal data of the user will be transferred to third parties without their prior consent, except where required by law or upon express request of the competent authorities.'
        ]
      },
      {
        niveau: 2,
        titre: 'Hyperlinks and third-party services',
        paragraphes: [
          'The datasectoriel.com website may contain links redirecting to third-party sites. These links are provided for information purposes and may not incur the liability of datasectoriel or NeuriaLabs regarding the content, practices or policies implemented by these external sites.',
          'NeuriaLabs does not control the services offered by third parties and recommends that users read the terms and conditions of use and the privacy policies of the platforms concerned before any interaction.',
          'Any insertion of hyperlinks to the datasectoriel.com website without express authorisation is strictly prohibited.'
        ]
      },
      {
        niveau: 2,
        titre: 'Modification of the Terms and Conditions of Use',
        paragraphes: [
          'NeuriaLabs reserves the right to modify these terms and conditions at any time in order to adapt them to legal developments, technological advances or new commercial practices.',
          'Any substantial modification will be published on the site. Users are invited to consult the terms and conditions regularly in order to be aware of any updates.',
          'Continued use of the site after the publication of the modifications constitutes acceptance of the new terms.'
        ]
      },
      {
        niveau: 2,
        titre: 'Applicable law and competent jurisdiction',
        paragraphes: [
          'These terms and conditions are governed by French law.',
          'In the event of a dispute relating to the interpretation, performance or validity of these terms and conditions, the parties undertake to attempt an amicable resolution before any legal action.',
          'Failing agreement, the dispute will be brought before the competent courts of the jurisdiction of the registered office of NeuriaLabs.'
        ]
      },
      {
        niveau: 2,
        titre: 'Contact',
        paragraphes: [
          'Any question relating to these terms and conditions or any request for further information may be sent to datasectoriel or NeuriaLabs at the following addresses:',
          'Email: contact@datasectoriel.com or contact@neurialabs.com'
        ]
      }
    ],
    texte: 'This document is made available on request from Datasectoriel.',
    lien: 'Contact us',
    cookies: 'Cookie policy',
    cookiesBlocs: [
      {
        niveau: 2,
        titre: 'Introduction',
        paragraphes: [
          'This Cookie Management Policy is intended to inform users of the datasectoriel.com website (“the Site”) of the practices implemented regarding the collection, use and storage of cookies and other digital trackers.',
          'datasectoriel is a brand of NeuriaLabs, a company specialising in the design, development and integration of software solutions based on artificial intelligence.',
          'As part of its commitment to data protection and user privacy, NeuriaLabs ensures compliance with the regulations in force, in particular:'
        ],
        liste: [
          { libelle: '', texte: 'The General Data Protection Regulation (GDPR - EU Regulation 2016/679)' },
          { libelle: '', texte: 'The ePrivacy Directive' },
          { libelle: '', texte: 'The California Consumer Privacy Act (CCPA)' },
          { libelle: '', texte: 'Any other applicable regulation depending on the user’s jurisdiction' }
        ],
        apres: [
          'The objective of this policy is to guarantee total transparency regarding the purposes of the cookies used and to provide users with mechanisms to control and manage their preferences.',
          'Continued browsing of the Site implies acceptance of the terms described in this policy, subject to the explicit consent required for certain categories of cookies.'
        ]
      },
      {
        niveau: 2,
        titre: 'Definition and nature of cookies',
        paragraphes: [
          'Cookies are small files stored on the user’s device (computer, smartphone, tablet) when they visit a website. These files contain information that makes it possible to improve browsing, personalise the content displayed and analyse interactions with the Site.',
          'There are several categories of cookies, differentiated according to their purpose and how they operate:'
        ],
        liste: [
          { libelle: 'Strictly necessary cookies:', texte: 'These cookies are essential for the proper functioning of the Site. In particular, they make it possible to manage user sessions, ensure the security of transactions and facilitate browsing. They do not require the user’s consent.' },
          { libelle: 'Analytical and statistical cookies:', texte: 'These cookies are used to measure the Site’s audience, analyse browsing behaviour and optimise the performance of online services. Their activation is subject to the user’s prior consent.' },
          { libelle: 'Personalisation cookies:', texte: 'These cookies make it possible to adapt the display of the Site according to the user’s preferences, in particular regarding language, layout or suggested content. They require the user’s express authorisation.' },
          { libelle: 'Security cookies:', texte: 'These cookies protect the Site against intrusion attempts, fraud and other malicious activities. They are essential to guarantee the integrity of digital communications.' },
          { libelle: 'Third-party cookies:', texte: 'These cookies are placed by partners of datasectoriel and NeuriaLabs, in particular for the integration of multimedia content, social media sharing buttons or the management of interactions with external platforms. They require the user’s agreement.' }
        ],
        apres: [
          'Each category of cookies fulfils a specific function and is managed in accordance with the principles of data minimisation and proportionality defined by the applicable regulations.'
        ]
      },
      {
        niveau: 2,
        titre: 'Objectives and purposes of the cookies used by datasectoriel',
        paragraphes: ['The use of cookies on the Site pursues several essential objectives, including:']
      },
      {
        niveau: 3,
        titre: 'Guaranteeing the security and integrity of the Site',
        paragraphes: ['Technical and security cookies make it possible to authenticate users, prevent fraudulent access and protect the integrity of browsing sessions. They are used in particular for:'],
        liste: [
          { libelle: '', texte: 'Session management and the identification of authorised users.' },
          { libelle: '', texte: 'The detection of intrusion attempts and the prevention of computer attacks.' },
          { libelle: '', texte: 'Access control to the secure areas of the Site.' }
        ],
        apres: ['These cookies are enabled by default and cannot be disabled, as they are essential to the operation of the Site.']
      },
      {
        niveau: 3,
        titre: 'Improving the user experience',
        paragraphes: ['Personalisation cookies make it possible to adapt the Site’s interface to the individual preferences of users. Their activation allows:'],
        liste: [
          { libelle: '', texte: 'The storage of language choices and display settings.' },
          { libelle: '', texte: 'The optimisation of the browsing experience according to user preferences.' },
          { libelle: '', texte: 'The improvement of the Site’s usability and accessibility.' }
        ],
        apres: ['These cookies are only activated with the user’s consent.']
      },
      {
        niveau: 3,
        titre: 'Analysing traffic and use of the Site',
        paragraphes: ['Analytical cookies collect statistical data on how users interact with the Site. In particular, they make it possible to:'],
        liste: [
          { libelle: '', texte: 'Measure the number of visitors and the most viewed pages.' },
          { libelle: '', texte: 'Assess the performance of the content and services offered.' },
          { libelle: '', texte: 'Detect any malfunctions and improve the overall user experience.' }
        ],
        apres: ['These cookies are subject to the user’s prior consent and may be disabled at any time via the preference management tool.']
      },
      {
        niveau: 3,
        titre: 'Enabling the integration of third-party content',
        paragraphes: ['Certain external services integrated into the Site, such as videos, interactive maps or social media sharing buttons, require the installation of third-party cookies. These cookies allow:'],
        liste: [
          { libelle: '', texte: 'The display and playback of content hosted on external platforms.' },
          { libelle: '', texte: 'Interaction with social networks or partner services.' },
          { libelle: '', texte: 'The synchronisation of user preferences across different platforms.' }
        ],
        apres: ['The activation of these cookies depends on the user’s consent, who may choose to accept or refuse them via the cookie management module.']
      },
      { niveau: 2, titre: 'Cookie retention period', paragraphes: [] },
      {
        niveau: 3,
        titre: 'General retention principles',
        paragraphes: [
          'The retention period of cookies varies according to their nature and purpose. In accordance with the regulations in force, in particular the GDPR and the ePrivacy Directive, NeuriaLabs undertakes to retain cookies only for the time strictly necessary for the purposes pursued.',
          'Cookies may be stored on the user’s device for different periods depending on their type:'
        ],
        liste: [
          { libelle: 'Session cookies:', texte: 'temporary and automatically deleted as soon as the user closes their browser.' },
          { libelle: 'Persistent cookies:', texte: 'remain stored after the browser is closed, with a lifespan limited to a maximum of 13 months, after which they are automatically deleted or renewed with new consent from the user.' }
        ]
      },
      {
        niveau: 3,
        titre: 'Criteria determining the retention period',
        paragraphes: ['The retention period of cookies is defined according to:'],
        liste: [
          { libelle: '', texte: 'The specific purpose of the cookie (technical operation, audience measurement, personalisation, etc.).' },
          { libelle: '', texte: 'The legal and regulatory requirements applicable to the jurisdictions in which NeuriaLabs operates.' },
          { libelle: '', texte: 'The potential risks to users’ privacy, ensuring that the collection and storage of personal data is minimised.' }
        ]
      },
      {
        niveau: 3,
        titre: 'Deletion and management of cookies',
        paragraphes: [
          'Users have the option of managing and deleting the cookies stored on their device via their browser settings or via the preference management tool made available on the datasectoriel.com website. However, deleting cookies may affect certain features of the Site.'
        ]
      },
      { niveau: 2, titre: 'Managing cookie preferences', paragraphes: [] },
      {
        niveau: 3,
        titre: 'Consent and user choices',
        paragraphes: [
          'NeuriaLabs respects the principle of informed consent, in accordance with the obligations of the GDPR and the ePrivacy Directive. On their first visit to the Site, the user is informed of the use of cookies and may choose to accept or refuse certain categories of cookies via a consent banner displayed on screen.',
          'The user has the following options:'
        ],
        liste: [
          { libelle: '', texte: 'Accept all cookies, including those intended for analysis and personalisation.' },
          { libelle: '', texte: 'Refuse all non-essential cookies, keeping only those strictly necessary for the operation of the Site.' },
          { libelle: '', texte: 'Customise their choices by enabling or disabling certain categories of cookies according to their preferences.' }
        ]
      },
      {
        niveau: 3,
        titre: 'Changing cookie settings at any time',
        paragraphes: ['Users may change their preferences at any time via:'],
        liste: [
          { libelle: '', texte: 'A cookie management module permanently accessible at the bottom of the Site.' },
          { libelle: '', texte: 'Their browser settings, which make it possible to block, delete or limit certain types of cookies.' }
        ]
      },
      {
        niveau: 3,
        titre: 'Consequences of refusing cookies',
        paragraphes: ['Refusing certain cookies may result in:'],
        liste: [
          { libelle: '', texte: 'A degraded user experience, in particular regarding display and interactivity.' },
          { libelle: '', texte: 'The inability to access certain advanced features requiring technical or personalisation cookies.' },
          { libelle: '', texte: 'A limitation of the analysis tools enabling datasectoriel and NeuriaLabs to improve the quality of the Site.' }
        ],
        apres: ['However, refusing analytical, advertising and personalisation cookies does not affect basic browsing of the Site.']
      },
      { niveau: 2, titre: 'Changes and amendments to the Cookie Management Policy', paragraphes: [] },
      {
        niveau: 3,
        titre: 'Commitment to regular updates',
        paragraphes: [
          'NeuriaLabs undertakes to update this Cookie Management Policy in line with technological, legislative and regulatory developments. These updates are intended to guarantee ongoing compliance with legal obligations and to ensure total transparency towards users.'
        ]
      },
      {
        niveau: 3,
        titre: 'How changes are notified',
        paragraphes: ['In the event of a substantial change to cookie practices, datasectoriel and NeuriaLabs will inform users via:'],
        liste: [
          { libelle: '', texte: 'An update to this policy, accessible at any time on the Site.' },
          { libelle: '', texte: 'A notice displayed on the Site, in particular via a notification banner or a contextual message.' },
          { libelle: '', texte: 'An email notification, only for users who have agreed to receive communications about changes to the privacy policy.' }
        ],
        apres: ['It is up to users to consult this policy regularly in order to keep informed of any adjustments.']
      },
      {
        niveau: 3,
        titre: 'Impact of changes on the user’s choices',
        paragraphes: [
          'Where substantial changes affect the way cookies are managed, new consent may be requested from users. They will have the opportunity to review and change their preferences before any activation of the new cookies concerned.'
        ]
      },
      { niveau: 2, titre: 'Contact and exercise of rights', paragraphes: [] },
      {
        niveau: 3,
        titre: 'Users’ rights regarding cookies',
        paragraphes: ['In accordance with the GDPR, the CCPA and other regulations in force, users have several fundamental rights regarding the management of cookies and the processing of their personal data:'],
        liste: [
          { libelle: '', texte: 'Right of access' },
          { libelle: '', texte: 'Right of rectification' },
          { libelle: '', texte: 'Right to erasure (“right to be forgotten”)' },
          { libelle: '', texte: 'Right to restriction of processing' },
          { libelle: '', texte: 'Right to object' },
          { libelle: '', texte: 'Right to portability' }
        ]
      },
      {
        niveau: 3,
        titre: 'How to exercise these rights',
        paragraphes: ['To exercise their rights or for any question relating to the management of cookies, users may contact datasectoriel or NeuriaLabs via:'],
        liste: [
          { libelle: 'Dedicated email address:', texte: 'contact@datasectoriel.com or contact@neurialabs.com' }
        ],
        apres: ['Any request must be accompanied by proof of identity in order to guarantee the security of exchanges and prevent any fraudulent access to personal data.']
      },
      {
        niveau: 3,
        titre: 'Remedies in the event of a dispute',
        paragraphes: [
          'If users consider that their rights are not being respected or that datasectoriel and NeuriaLabs are not processing their data in accordance with the regulations in force, they have the option of lodging a complaint with the competent data protection authority (CNIL, PFPDT, FTC, ICO, etc.).',
          'Before any official complaint, it is recommended to contact datasectoriel or NeuriaLabs directly in order to attempt an amicable resolution of the dispute.'
        ]
      }
    ],
    confidentialiteBlocs: [
      {
        niveau: 2,
        titre: 'Introduction',
        paragraphes: [
          'datasectoriel is a brand of NeuriaLabs, a company specialising in the design, development and integration of software solutions based on artificial intelligence.',
          'This privacy policy sets out the principles and commitments of NeuriaLabs regarding the protection of personal data collected in connection with the use of the datasectoriel platform.',
          'In accordance with the applicable legislation, in particular the General Data Protection Regulation (GDPR – EU Regulation 2016/679), the California Consumer Privacy Act (CCPA), as well as the various national laws governing data protection, NeuriaLabs guarantees that all collection, use, retention and transmission of personal data is carried out in strict compliance with legal obligations and international security and confidentiality standards.'
        ]
      },
      {
        niveau: 3,
        titre: 'Objectives of the policy',
        paragraphes: [
          'This privacy policy applies to any person interacting with datasectoriel and NeuriaLabs, whether via the datasectoriel.com website, the associated platforms, professional services or within the framework of contractual and institutional relationships.'
        ]
      },
      {
        niveau: 2,
        titre: 'Data collected and purposes of processing',
        paragraphes: [
          'NeuriaLabs collects only the data strictly necessary for its activities and ensures that it never carries out excessive or disproportionate collection of information.',
          'The data collected may include:'
        ],
        liste: [
          { libelle: 'Identification and contact information:', texte: 'surname, first name, role, company, professional email address, telephone number.' },
          { libelle: 'Information relating to professional exchanges:', texte: 'communications with our teams, requests for information, contractual interactions.' },
          { libelle: 'Technical and browsing information:', texte: 'IP address, browser type, analytics data, activity logs, exclusively for security purposes and to improve services.' },
          { libelle: '', texte: 'Any other data voluntarily provided by users in the course of their interactions with datasectoriel or NeuriaLabs.' }
        ]
      },
      {
        niveau: 3,
        titre: 'Purposes of processing',
        paragraphes: ['The data collected is processed for the following purposes:'],
        liste: [
          { libelle: '', texte: 'Management and monitoring of professional, institutional and contractual relationships.' },
          { libelle: '', texte: 'Performance of contractual obligations and provision of services.' },
          { libelle: '', texte: 'Securing digital interactions and preventing cyber threats.' },
          { libelle: '', texte: 'Compliance with applicable legal and regulatory obligations.' }
        ]
      },
      {
        niveau: 2,
        titre: 'Legal basis for processing',
        paragraphes: ['All collection and processing of personal data carried out by NeuriaLabs is based on clearly defined legal grounds:'],
        liste: [
          { libelle: '', texte: 'The consent of the data subject, where such consent is required.' },
          { libelle: '', texte: 'The performance of a contract requiring the use of the data to fulfil the agreed obligations.' },
          { libelle: '', texte: 'Compliance with a legal or regulatory obligation requiring the retention or transmission of certain information.' },
          { libelle: '', texte: 'The legitimate interest of NeuriaLabs in ensuring the effective management of its services, the protection of its systems and the optimisation of the user experience.' }
        ]
      },
      {
        niveau: 2,
        titre: 'Sharing and transfer of data',
        paragraphes: [
          'NeuriaLabs undertakes never to sell or rent personal data.',
          'However, certain information may be shared in the following cases:'
        ],
        liste: [
          { libelle: 'Technical partners or subcontractors:', texte: 'solely for the performance of the services, subject to contractual confidentiality and compliance clauses.' },
          { libelle: '', texte: 'Competent administrative or judicial authorities, where the law requires the disclosure of information.' },
          { libelle: '', texte: 'Third-party institutions, in the context of merger, acquisition or restructuring operations, subject to the maintenance of the same data protection standards.' }
        ]
      },
      {
        niveau: 2,
        titre: 'Security and data protection',
        paragraphes: [
          'NeuriaLabs implements strict measures to protect personal data against any unauthorised access, alteration, loss or disclosure.',
          'These measures include in particular:'
        ],
        liste: [
          { libelle: '', texte: 'The use of advanced encryption protocols to protect communications and databases.' },
          { libelle: '', texte: 'The implementation of a strengthened authentication system and access control for sensitive information.' },
          { libelle: '', texte: 'Continuous monitoring of digital infrastructures in order to detect and neutralise any intrusion attempt.' },
          { libelle: '', texte: 'Regular cybersecurity audits and penetration tests to assess the effectiveness of the protection measures.' }
        ]
      },
      {
        niveau: 2,
        titre: 'Data retention period',
        paragraphes: [
          'The personal data collected is retained only for the time necessary for the purposes for which it was obtained, in accordance with legal obligations.',
          'The retention criteria include in particular:'
        ],
        liste: [
          { libelle: '', texte: 'The monitoring of professional and contractual relationships.' },
          { libelle: '', texte: 'Compliance with regulatory retention requirements.' },
          { libelle: '', texte: 'Requirements relating to compliance, risk management and archiving.' }
        ],
        apres: ['At the end of these periods, the data is permanently deleted or anonymised for exclusively analytical purposes.']
      },
      {
        niveau: 2,
        titre: 'Rights of data subjects',
        paragraphes: ['Any person whose data is collected by NeuriaLabs benefits from the following rights:'],
        liste: [
          { libelle: 'Right of access:', texte: 'obtain a copy of the data held.' },
          { libelle: 'Right of rectification:', texte: 'correct or complete inaccurate information.' },
          { libelle: 'Right to erasure (“right to be forgotten”):', texte: 'request the deletion of the data, subject to legal conditions.' },
          { libelle: 'Right to restriction of processing:', texte: 'temporarily restrict the use of the data.' },
          { libelle: 'Right to object:', texte: 'refuse processing based on a legitimate interest.' },
          { libelle: 'Right to portability:', texte: 'receive the data in a structured and transferable format.' }
        ]
      },
      {
        niveau: 3,
        titre: 'Exercising rights',
        paragraphes: [
          'To exercise their rights, data subjects may send a written request to: contact@datasectoriel.com or contact@neurialabs.com',
          'Any request will be processed within a maximum of 30 days of receipt, except in exceptional circumstances requiring additional time, in which case a notification will be sent to the data subject.'
        ]
      },
      {
        niveau: 2,
        titre: 'Cookies and tracking technologies',
        paragraphes: [
          'NeuriaLabs uses cookies and similar technologies in order to ensure the security of digital interactions, optimise the user experience and guarantee the technical compliance of the datasectoriel.com website.',
          'For more information on our use of cookies, please consult our Cookie Management Policy.'
        ]
      },
      {
        niveau: 2,
        titre: 'Contact',
        paragraphes: ['For any question relating to this policy: contact@datasectoriel.com or contact@neurialabs.com']
      }
    ],
    mentionsBlocs: [
      {
        niveau: 2,
        titre: 'Identification of the publisher',
        paragraphes: [
          'datasectoriel is a brand of NeuriaLabs, a company specialising in the design, development and integration of software solutions based on artificial intelligence.',
          'datasectoriel is a platform published and operated by NeuriaLabs, registered with the Trade and Companies Register of Coutances under number 953 293 669, whose registered office is located at 510 avenue des Platanes, 50000 Saint-Lô, France.',
          'For all correspondence, users may contact datasectoriel or NeuriaLabs at the following address: contact@datasectoriel.com or contact@neurialabs.com',
          'The publisher of the site is responsible for the content distributed and for compliance with the applicable regulations on digital communication.',
          'NeuriaLabs reserves the right to modify, at any time and without notice, all or part of this legal notice in order to ensure its compliance with regulatory and technical developments.'
        ]
      },
      {
        niveau: 2,
        titre: 'Website hosting',
        paragraphes: [
          'Hosting of the datasectoriel.com website is provided by Hostinger International Ltd, a company specialising in the management of secure digital platforms that comply with international cybersecurity and data protection standards.'
        ],
        adresse: ['Hostinger International Ltd', '61 Lordou Vironos Street, Larnaca, 6023, Cyprus', 'Email address: support@hostinger.com'],
        apres: [
          'The host guarantees optimal availability of the site and advanced protection against intrusions, cyberattacks and unauthorised alterations. It is subject to the legal obligations of confidentiality and integrity of the hosted data, in accordance with the GDPR and the applicable European standards.'
        ]
      },
      {
        niveau: 2,
        titre: 'Intellectual property',
        paragraphes: [
          'All the constituent elements of the datasectoriel website (texts, visuals, videos, graphics, logos, interfaces, databases, source code, layouts and software structures) are the exclusive property of NeuriaLabs and are protected by the legal provisions relating to intellectual property and copyright.',
          'Any reproduction, representation, distribution, modification or exploitation, in whole or in part, without the prior written authorisation of NeuriaLabs, is strictly prohibited and exposes its author to legal proceedings.',
          'Use of the site grants no intellectual property rights to the user, who undertakes not to copy, adapt, translate or exploit all or part of its content without express authorisation.',
          'The trademarks, logos and distinctive signs appearing on the site, including datasectoriel and NeuriaLabs, are protected as registered trademarks or commercial assets. Any breach of these provisions may give rise to legal action and to the civil and criminal liability of the user.'
        ]
      },
      { niveau: 2, titre: 'Liability', paragraphes: [] },
      {
        niveau: 3,
        titre: 'Published content',
        paragraphes: [
          'NeuriaLabs ensures the quality, clarity and reliability of the information made available on the datasectoriel website. However, this information is provided for guidance only and constitutes neither a contractual commitment nor a guarantee of absolute accuracy.',
          'NeuriaLabs may not be held liable for any errors, omissions or inaccuracies, nor for the consequences related to the use of the information present on the site. Users are invited to verify the relevance of the data communicated before making any decision.',
          'NeuriaLabs reserves the right to update, modify or delete any content, without notice, without this incurring any liability on its part.'
        ]
      },
      {
        niveau: 3,
        titre: 'Technical liability and access to the site',
        paragraphes: [
          'NeuriaLabs implements all the technical and organisational means necessary to ensure the availability, security and reliability of the datasectoriel website. However, access to the site may be temporarily interrupted for maintenance, updates or technical reasons beyond the publisher’s control.',
          'The user acknowledges accessing the site under their sole responsibility and undertakes to protect their equipment and data against any computer risk (viruses, intrusions, data loss, etc.).',
          'NeuriaLabs may not be held liable for direct or indirect damage related to:'
        ],
        liste: [
          'the temporary or prolonged unavailability of the site,',
          'technical malfunctions,',
          'external computer attacks,',
          'or any non-compliant use of the site.'
        ]
      },
      {
        niveau: 3,
        titre: 'Hyperlinks and third-party services',
        paragraphes: [
          'The datasectoriel.com website may contain links redirecting to third-party sites. These links are provided for information purposes only and do not imply any endorsement of the content, policy or practices of these external sites.',
          'NeuriaLabs accepts no liability regarding access to, the content of, or the consequences related to consulting these external resources. It is up to each user to verify the reliability and compliance of the sites to which they choose to navigate.'
        ]
      },
      {
        niveau: 2,
        titre: 'Protection of personal data',
        paragraphes: [
          'NeuriaLabs applies a strict personal data protection policy, in compliance with the requirements of the General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA) and any applicable regulations regarding digital privacy.',
          'The terms of collection, processing and retention of data are set out in the Privacy Policy available on the site. Users have rights of access, rectification, erasure, portability and objection, which they may exercise by writing to contact@datasectoriel.com or contact@neurialabs.com.',
          'NeuriaLabs undertakes to respond to any request within the legal time limits and to ensure transparent and secure processing of personal information.'
        ]
      },
      {
        niveau: 2,
        titre: 'Applicable law and competent jurisdiction',
        paragraphes: [
          'This legal notice is governed by French law. In the event of a dispute relating to its interpretation or performance, and failing an amicable resolution, exclusive jurisdiction is granted to the courts of Paris.',
          'In the event of an international contractual relationship, specific provisions may apply, subject to a written agreement between the parties.'
        ]
      },
      {
        niveau: 2,
        titre: 'Contact and reporting',
        paragraphes: [
          'Any question or complaint relating to the content of the datasectoriel website may be sent to: contact@datasectoriel.com or contact@neurialabs.com',
          'NeuriaLabs undertakes to process any request as quickly as possible and to take the appropriate measures in the event of a report of unlawful content or content that does not comply with the regulations in force.'
        ]
      }
    ]
  },
  secteurs: ['Medicine and health','Pharmacy and biotechnology','Life sciences','Chemistry and materials','Energy and environment','Industrial engineering','Aeronautics and space','Automotive and transport','Construction and civil engineering','Electronics and robotics','IT and telecommunications','Cybersecurity','AI and data science','Finance and economics','Banking and insurance','Law and legal affairs','Public administration','Defence and security','Agriculture and agri-food','Fisheries and marine resources','Education and teaching','Humanities and social sciences','Psychology and neuroscience','Architecture and urban planning','Art, culture and heritage','Music and performing arts','Film and audiovisual','Journalism and media','Marketing and advertising','Luxury, fashion and design','Sport and performance','Nutrition and dietetics','Tourism and hospitality','Real estate and urban planning','Logistics and maritime transport','International trade','Renewable energy','Research and innovation','Climate and ecology','Political science and IR'],
  taches: ['cooking and food preparation','cleaning and maintenance','tidying up','laundry and ironing','gardening','DIY and everyday repairs','furniture assembly','vehicle maintenance','animal care','baking and pastry','product restocking','craftwork','farming','product preparation and packaging','order picking','sorting operations','warehouse and logistics activities','certain industrial operations'],
  tachesBesoin: ['Cooking and food preparation','Cleaning and maintenance','Tidying up','Laundry and ironing','Gardening','DIY and everyday repairs','Furniture assembly','Vehicle maintenance','Animal care','Baking and pastry','Product restocking','Craftwork','Farming','Product preparation and packaging','Order picking','Sorting operations','Warehouse and logistics','Industrial operations'],
  etapes: [
    { titre: 'Defining the project', paragraphes: ['Together we establish the scope of the collection: sector, content, tasks, volume, languages, duration, formats, expected quality and conditions of use.','This stage makes it possible to determine the information required for the production, verification and classification of the data.'] },
    { titre: 'Selecting participants', paragraphes: ['We identify the experts, individuals, professionals or companies able to produce the requested content.','Where the nature of the project requires specific qualifications, professional credentials are verified before production begins.'] },
    { titre: 'Producing the recordings', paragraphes: ['Each participant receives precise instructions regarding the task to be performed, the authorised equipment, the position of the device, the expected duration, safety rules, the elements that must not be recorded and the information to be transmitted with the video.','Depending on the needs of the project, the recordings may be accompanied by data relating to movement, the equipment used and the technical conditions of capture.'] },
    { titre: 'Describing the data', paragraphes: ['Each file is associated with the information required to understand and classify it. This information may include:'], liste: ['the nature of the task','the various actions performed','the objects handled','the tools used','the length of the sequence','the type of environment','the language','the production date','the recording equipment','the technical characteristics of the file'], apres: ['Where the project requires it, the videos may also be accompanied by annotations describing the main stages of the task.'] },
    { titre: 'Verifying the files', paragraphes: ['Each recording is examined before integration. The verification covers in particular:'], liste: ['compliance of the task','adherence to the instructions','image quality','stability of the capture','continuity of the sequence','consistency of the information provided','the absence of duplicated, modified or non-compliant content','the possible presence of personal or confidential elements'], apres: ['Files that do not meet the defined criteria are rejected, corrected or replaced.'] },
    { titre: 'Processing sensitive elements', paragraphes: ['Before integration, the videos are processed in order to remove or mask information that is not necessary for their use. This processing may concern in particular:'], liste: ['faces','licence plates','screens','identity documents','bank cards','personal contact details','confidential information','any element making it possible to directly identify a person'], apres: ['The recording rules prohibit the capture of minors, medical or intimate situations, passwords, sensitive administrative documents and any person who has not been informed of the recording.'] },
    { titre: 'Classification and traceability', paragraphes: ['Accepted videos are classified according to a precise structure that may include:'], liste: ['the sector','the sub-sector','the type of content','the nature of the task','the language','the duration','the date','the format','the equipment used','the recording environment','the objects and tools present'], apres: ['Each file receives a unique digital fingerprint enabling it to be identified, its integrity verified and its use tracked.'] },
    { titre: 'Making the data available', paragraphes: ['The data is made available to you in a secure environment, in accordance with the volume and conditions provided for by the licence taken out.','The licence specifies the corpora concerned, the authorised uses, the duration of access and the conditions of download and exploitation.'] }
  ]
};

export default en;
